import { inject, Injectable } from '@angular/core';

import { Sha256 } from '@aws-crypto/sha256-js';
import { Store } from '@ngrx/store';
import { from, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import {
    base64UrlEncodeBytes,
    base64UrlEncodeStr,
    EnvironmentConfigService,
    GenerateGuid,
    Logout,
    sessionStorageHelper,
    ShCacheInterceptor,
    TokenSyncService,
    User,
    UserService,
    UserTokens
} from '@stylehaven/components';
import { SetOauth, UnsetOauth } from '../../../state/oauth/oauth.actions';
import { getOauthStateById } from '../../../state/oauth/oauth.selector';
import { SharedApplicationState } from '../../../state/shared-state.interface';
import { SetUser } from '../../../state/user/user.actions';
import { AuthenticationService } from '../services/authentication/authentication.service';
import { AuthorizationService } from '../services/authorization/authorization.service';

@Injectable({ providedIn: 'root' })
export class AuthenticationSandbox {
    private readonly tokenSyncService: TokenSyncService = inject(TokenSyncService);
    private readonly cacheInterceptor: ShCacheInterceptor = inject(ShCacheInterceptor);

    constructor(
        private readonly authenticationService: AuthenticationService,
        private readonly authorizationService: AuthorizationService,
        private readonly configService: EnvironmentConfigService,
        private readonly store: Store<SharedApplicationState>,
        private readonly userService: UserService
    ) {}

    getStoredEntryNavigationUrl(stateKey: string): Observable<string | null> {
        return this.store
            .select(getOauthStateById(stateKey))
            .pipe(map(oauthState => oauthState?.entryNavigationUrl ?? null));
    }

    getStoredVerifier(): string | null {
        return sessionStorageHelper.get('pkce_verifier');
    }

    private createNonce(): string {
        /*
         * This alphabet is from:
         * https://tools.ietf.org/html/rfc7636#section-4.1
         *
         * [A-Z] / [a-z] / [0-9] / "-" / "." / "_" / "~"
         */
        const unreserved = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
        let size = 45;
        let id = '';

        const crypto = typeof self === 'undefined' ? null : self.crypto || (self as any).msCrypto;
        if (crypto) {
            let bytes = new Uint8Array(size);
            crypto.getRandomValues(bytes);

            // Needed for IE
            if (!bytes.map) {
                (bytes as any).map = Array.prototype.map;
            }

            bytes = bytes.map(x => unreserved.charCodeAt(x % unreserved.length));
            id = String.fromCharCode.apply(null, Array.from(bytes));
        } else {
            while (0 < size--) {
                id += unreserved[(Math.random() * unreserved.length) | 0];
            }
        }
        return base64UrlEncodeStr(id);
    }

    private createChallengeVerifierPairForPKCE(): Observable<[string, string]> {
        const verifier: string = this.createNonce();

        const hash = new Sha256();
        hash.update(verifier);
        return from(hash.digest()).pipe(map(digest => [base64UrlEncodeBytes(digest), verifier] as [string, string]));
    }

    getNewStatefulOauthUrl(entryNavigationUrl: string): Observable<string> {
        const stateKey: string = GenerateGuid();

        return this.createChallengeVerifierPairForPKCE().pipe(
            switchMap(([challenge, verifier]) => {
                this.store.dispatch(new SetOauth({ stateKey, entryNavigationUrl }));
                sessionStorageHelper.set('pkce_verifier', verifier);
                return this.authenticationService.getOauthUrl(stateKey, challenge);
            })
        );
    }

    hasExternalAuth(): Observable<boolean> {
        return this.authenticationService.hasDirectOAuth();
    }

    isOfflineEnvironment(): boolean {
        return this.configService.getInstant('env') === 'offline' || false;
    }

    getOauthLogoutUrl(): Observable<string> {
        return this.authenticationService.oauthLogoutUrl$;
    }

    public getOauthLogoutUrlWithRedirectStream(redirectUri: string): Observable<string> {
        return this.authenticationService.getOauthLogoutUrlWithRedirectStream(redirectUri);
    }

    oauth(token: string): Observable<boolean> {
        return this.authenticationService.requestAccessToken(token, this.getStoredVerifier()).pipe(
            map(userToken => this.handleToken(userToken)),
            switchMap(isLoggedIn => this.getUserInfo().pipe(map(() => isLoggedIn))),
            catchError((error: Error) => this.handleError(error))
        );
    }

    login(username: string, password: string): Observable<boolean> {
        return this.authenticationService.login(username, password).pipe(
            map(userToken => this.handleToken(userToken)),
            switchMap(isLoggedIn => this.getUserInfo().pipe(map(() => isLoggedIn))),
            catchError((error: Error) => this.handleError(error))
        );
    }

    logout(): void {
        this.setLoggedOut();
    }

    private handleToken(userTokens: UserTokens): boolean {
        if (userTokens.access_token) {
            this.tokenSyncService.setTokens(userTokens);
            return true;
        } else {
            return false;
        }
    }

    private getUserInfo(): Observable<void> {
        return this.authorizationService.getUserInfo().pipe(take(1), map(this.handleUserName.bind(this)));
    }

    private handleUserName(mediaHavenUser: User): void {
        this.store.dispatch<SetUser>(new SetUser(mediaHavenUser));
    }

    private handleError(error: Error): Observable<never> {
        this.logout();

        return throwError(error);
    }

    private setLoggedOut(): void {
        this.cacheInterceptor.clearCache();
        this.userService.clearCurrentUser();
        this.authorizationService.onLogout();
        this.store.dispatch(new Logout());
        this.tokenSyncService.clearTokens();
    }

    unsetOauthRedirect(): void {
        this.store.dispatch(new UnsetOauth());
        if (sessionStorageHelper.has('pkce_verifier')) {
            sessionStorageHelper.remove('pkce_verifier');
        }
    }
}
